import { useState, useRef } from "react";
import { useAuth } from "@clerk/react";
import { Upload, Download, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorBody, getApiErrorMessage } from "@/lib/api-error";
import { apiUrl } from "@/lib/apiBase";

type ParsedRow = {
  className: string;
  yearLevel: string;
  courseName: string;
  courseColor: string;
  error?: string;
};

function parseCSV(text: string): ParsedRow[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];

  const header = lines[0].toLowerCase().split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  const colIdx = (names: string[]) => names.map((n) => header.indexOf(n)).find((i) => i >= 0) ?? -1;

  const classIdx = colIdx(["class", "class_name", "classname", "class name"]);
  const yearIdx = colIdx(["year_level", "year level", "year", "grade"]);
  const courseIdx = colIdx(["course", "course_name", "coursename", "subject"]);
  const colorIdx_ = colIdx(["color", "colour", "course_color", "course_colour"]);

  if (classIdx === -1 || courseIdx === -1) return [];

  const splitLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (const ch of line) {
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === "," && !inQuotes) { result.push(current.trim()); current = ""; }
      else { current += ch; }
    }
    result.push(current.trim());
    return result;
  };

  return lines.slice(1).map((line) => {
    const cols = splitLine(line);
    const className = (cols[classIdx] ?? "").replace(/^"|"$/g, "").trim();
    const yearLevel = yearIdx >= 0 ? (cols[yearIdx] ?? "").replace(/^"|"$/g, "").trim() : "";
    const courseName = (cols[courseIdx] ?? "").replace(/^"|"$/g, "").trim();
    const courseColor = colorIdx_ >= 0 ? (cols[colorIdx_] ?? "").replace(/^"|"$/g, "").trim() : "";
    const error = !className || !courseName ? "Missing class or subject" : undefined;
    return { className, yearLevel, courseName, courseColor, error };
  });
}

const TEMPLATE_CSV = `class,year_level,subject,color
10A,10,Mathematics,#1a73e8
10A,10,English,#e53935
10B,10,Mathematics,#1a73e8
10B,10,Science,#43a047
11 Bio,11,Biology,#00897b
`;

function downloadBlob(content: string, filename: string, type = "text/csv") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export type ImportClassesCoursesDialogProps = {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
};

type ImportResult = {
  coursesCreated: number;
  coursesReused: number;
  classesCreated: number;
  classesReused: number;
  linksCreated: number;
};

export function ImportClassesCoursesDialog({ open, onClose, onDone }: ImportClassesCoursesDialogProps) {
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { getToken } = useAuth();

  const reset = () => { setRows([]); setFileName(""); setResult(null); setImportError(null); };

  const handleFile = (file: File) => {
    setFileName(file.name);
    setResult(null);
    setImportError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setRows(parseCSV(text));
    };
    reader.readAsText(file);
  };

  const validRows = rows.filter((r) => !r.error);
  const invalidRows = rows.filter((r) => r.error);

  const handleImport = async () => {
    if (validRows.length === 0) return;
    setImporting(true);
    setImportError(null);
    try {
      const token = await getToken();
      let res: Response;
      try {
        res = await fetch(apiUrl("/api/school/import-classes-courses"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify({
            rows: validRows.map((r) => ({
              className: r.className,
              yearLevel: r.yearLevel || undefined,
              courseName: r.courseName,
              courseColor: r.courseColor || undefined,
            })),
          }),
        });
      } catch {
        // api-error-helper-exempt: network-level failure before any HTTP response (no ApiError to extract).
        const msg = "Couldn't reach the server. Please check your connection and try again.";
        setImportError(msg);
        toast({ title: "Import failed", description: msg, variant: "destructive" });
        return;
      }

      if (!res.ok) {
        // Raw fetch path: parse the body manually then run it through the
        // same shape the helper expects so the description matches every
        // other onError site in the app.
        let body: unknown = null;
        try { body = await res.json(); } catch { /* not JSON */ }
        const apiBody = getApiErrorBody(body);
        const fallback = `Import failed (${res.status} ${res.statusText || "error"}).`;
        const description = apiBody?.error ?? fallback;
        setImportError(description);
        toast({ title: "Import failed", description, variant: "destructive" });
        return;
      }

      const data = (await res.json()) as ImportResult;
      setResult(data);
      onDone();
    } catch (err) {
      // api-error-helper-exempt: raw fetch (not ApiError); helper produces a sensible fallback.
      const description = getApiErrorMessage(err, "Something went wrong while importing. Please try again.");
      setImportError(description);
      toast({ title: "Import failed", description, variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => { reset(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk import classes &amp; subjects</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-gray-500 -mt-1">
          Step 1 of school setup. Creates school-owned classes and subjects, and links them.
          Re-running the same file is safe — nothing is duplicated. Once this is done, use
          "Import students" to add students into these classes.
        </p>

        {/* Format guide */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">Required CSV format</p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs text-blue-600 hover:text-blue-700"
              onClick={() => downloadBlob(TEMPLATE_CSV, "classes-courses-template.csv")}
              data-testid="button-download-template"
            >
              <Download className="h-3.5 w-3.5" />
              Download template
            </Button>
          </div>
          <div className="font-mono text-xs bg-white border rounded p-2.5 text-gray-600 overflow-x-auto whitespace-pre">
            {`class,year_level,subject,color\n10A,10,Mathematics,#1a73e8\n10A,10,English,#e53935\n10B,10,Mathematics,#1a73e8`}
          </div>
          <ul className="mt-2.5 space-y-1 text-xs text-gray-500">
            <li><span className="font-medium text-gray-700">class</span>, <span className="font-medium text-gray-700">subject</span> — required</li>
            <li><span className="font-medium text-gray-700">year_level</span> — optional (e.g. 7, 10, 12)</li>
            <li><span className="font-medium text-gray-700">color</span> — optional hex like <code className="bg-gray-100 px-1 rounded">#1a73e8</code>. Used the first time a subject is seen.</li>
            <li>One row per class+subject pair. Repeat the class name on multiple rows to link several subjects to it.</li>
          </ul>
        </div>

        {/* File drop zone */}
        {!result && (
          <div
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-pink-400 hover:bg-pink-50/30 transition-colors"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="input-import-cc-csv"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            {fileName ? (
              <div className="flex flex-col items-center gap-1">
                <FileText className="h-8 w-8 text-pink-500 mb-1" />
                <p className="text-sm font-medium text-gray-900">{fileName}</p>
                <p className="text-xs text-gray-500">{rows.length} row{rows.length !== 1 ? "s" : ""} found — click to replace</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Upload className="h-8 w-8 text-gray-300 mb-1" />
                <p className="text-sm font-medium text-gray-700">Drop your CSV here, or click to browse</p>
                <p className="text-xs text-gray-400">.csv files only</p>
              </div>
            )}
          </div>
        )}

        {/* Result state */}
        {result && (
          <div className="space-y-2" data-testid="import-cc-result">
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div className="text-sm text-green-800 space-y-0.5">
                <p className="font-semibold">School structure imported</p>
                <p data-testid="text-cc-courses">
                  Subjects: {result.coursesCreated} created, {result.coursesReused} already existed
                </p>
                <p data-testid="text-cc-classes">
                  Classes: {result.classesCreated} created, {result.classesReused} already existed
                </p>
                <p data-testid="text-cc-links">
                  Class ↔ subject links added: {result.linksCreated}
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Next step: use "Import students" to add students into these classes.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Preview table */}
        {rows.length > 0 && !result && (
          <div className="max-h-56 overflow-y-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {["Class", "Year", "Subject", "Color"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-medium text-gray-600">{h}</th>
                  ))}
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row, i) => (
                  <tr key={i} className={row.error ? "bg-red-50" : ""}>
                    <td className="px-3 py-1.5 text-gray-900">
                      {row.className || <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-gray-500">{row.yearLevel || "—"}</td>
                    <td className="px-3 py-1.5">
                      {row.courseName ? (
                        <Badge
                          variant="secondary"
                          className="text-[10px] font-normal"
                          style={
                            row.courseColor
                              ? { backgroundColor: `${row.courseColor}20`, color: row.courseColor }
                              : undefined
                          }
                        >
                          {row.courseName}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-gray-500">
                      {row.courseColor ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            className="inline-block w-3 h-3 rounded-full border border-gray-200"
                            style={{ backgroundColor: row.courseColor }}
                          />
                          <code className="text-[10px]">{row.courseColor}</code>
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {row.error && (
                        <span className="flex items-center gap-1 text-red-600">
                          <AlertCircle className="h-3.5 w-3.5" />{row.error}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 && !result && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />{validRows.length} valid
            {invalidRows.length > 0 && <><AlertCircle className="h-3.5 w-3.5 text-red-400 ml-2" />{invalidRows.length} will be skipped</>}
          </div>
        )}

        {importError && !result && (
          <div
            className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
            data-testid="text-cc-import-error"
          >
            <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{importError}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>{result ? "Close" : "Cancel"}</Button>
          {!result && (
            <Button
              onClick={handleImport}
              disabled={validRows.length === 0 || importing}
              className="bg-pink-500 hover:bg-pink-600 text-white"
              data-testid="button-confirm-cc-import"
              data-write-action
            >
              {importing ? "Importing…" : `Import ${validRows.length > 0 ? validRows.length : ""} row${validRows.length !== 1 ? "s" : ""}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
